
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useEventFetch = (eventId: string) => {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      console.log('Fetching event details for:', eventId);
      
      // First fetch the event data with host and attendee information
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select(`
          *,
          host:users!events_host_user_id_fkey(
            user_id,
            full_name,
            phone_number,
            avatar_url
          ),
          event_attendee(
            user_id,
            rsvp_status,
            attendee:users(
              user_id,
              full_name,
              phone_number,
              avatar_url
            )
          ),
          event_date_recommendations(
            recommendation_id,
            recommended_date,
            time_of_day,
            is_selected
          ),
          event_images(
            image_id,
            image_url,
            is_primary,
            display_order
          ),
          event_venue_recommendations(
            recommendation_id,
            is_selected,
            venue:venues(
              venue_id,
              name,
              address,
              image_url,
              style,
              price_range,
              website_url,
              instagram_handle,
              google_maps_link
            )
          )
        `)
        .eq('event_id', eventId)
        .maybeSingle();

      if (eventError) {
        console.error('Error fetching event:', eventError);
        throw eventError;
      }

      // Fetch additional attendees from event_invites table
      const { data: inviteData, error: inviteError } = await supabase
        .from('event_invites')
        .select(`
          invite_id,
          display_name,
          phone_number,
          rsvp_status,
          guest_avatar_url
        `)
        .eq('event_id', eventId)
        .not('rsvp_status', 'is', null);

      if (inviteError) {
        console.error('Error fetching invites:', inviteError);
      }

      // Then fetch the selected venue information separately for backward compatibility
      const { data: venueData, error: venueError } = await supabase
        .from('event_venue_recommendations')
        .select(`
          recommendation_id,
          venue:venues(
            venue_id,
            name,
            address
          )
        `)
        .eq('event_id', eventId)
        .eq('is_selected', true)
        .maybeSingle();

      if (venueError) {
        console.error('Error fetching venue:', venueError);
      }
      
      console.log('Event data:', eventData);
      console.log('Invite data:', inviteData);
      console.log('Selected venue:', venueData);
      console.log('Event venue recommendations:', eventData?.event_venue_recommendations);

      if (!eventData) {
        return null;
      }

      // Transform the data into the expected format
      const host = eventData.host;
      
      // Transform attendees from event_attendee table
      const attendeesFromEventAttendee = eventData.event_attendee.map((ea: any) => ({
        user_id: ea.attendee.user_id,
        full_name: ea.attendee.full_name,
        phone_number: ea.attendee.phone_number,
        avatar_url: ea.attendee.avatar_url,
        rsvp_status: ea.rsvp_status,
        isHost: ea.attendee.user_id === host.user_id
      }));
      
      // Transform attendees from event_invites table
      const attendeesFromInvites = inviteData ? inviteData.filter((invite: any) => 
        invite.rsvp_status === 'accepted' || invite.rsvp_status === 'yes'
      ).map((invite: any) => ({
        invite_id: invite.invite_id, // Keep invite_id instead of user_id for guests
        full_name: invite.display_name,
        phone_number: invite.phone_number,
        guest_avatar_url: invite.guest_avatar_url,
        rsvp_status: invite.rsvp_status === 'yes' ? 'accepted' : invite.rsvp_status,
        isHost: false
      })) : [];
      
      console.log('Attendees from event_attendee:', attendeesFromEventAttendee);
      console.log('Attendees from invites:', attendeesFromInvites);

      // Transform venue data correctly
      const selectedVenue = venueData?.venue ? {
        name: venueData.venue.name,
        address: venueData.venue.address
      } : null;

      // Get the selected date from event_date_recommendations
      const selectedDateRec = eventData.event_date_recommendations.find((rec: any) => rec.is_selected);
      const selectedDate = selectedDateRec ? selectedDateRec.recommended_date : null;

      // Combine host and all attendees
      const allParticipants = [
        {
          user_id: host.user_id, // Host has user_id
          full_name: host.full_name,
          avatar_url: host.avatar_url,
          phone_number: host.phone_number,
          rsvp_status: 'accepted', // Use 'accepted' for host for consistency
          isHost: true
        },
        ...attendeesFromEventAttendee.filter((a: any) => a.user_id !== host.user_id), // Filter out host from attendees
        ...attendeesFromInvites
      ];
      
      console.log('All participants:', allParticipants);

      return { 
        ...eventData,
        host,
        attendees: allParticipants,
        selectedVenue,
        selectedDate,
        additional_images: eventData.event_images || []
      };
    },
  });
};
